/**
 * NodeStreamProvider — the app-level, session-scoped WebRTC connection layer.
 *
 * WHY: previously every streaming page (Multi-Site, Alert-Hexagon, Cameras, …)
 * created its OWN NodeConnectionManager and tore it down on unmount, so every
 * navigation rebuilt the whole WS + PeerConnection + ICE handshake (5–10s stall).
 *
 * NOW: ONE shared NodeConnectionManager lives for the whole authenticated session
 * (this provider is mounted inside the protected Layout). Pages are *subscribers*,
 * not *owners*: they call subscribe()/unsubscribe() (ref-counted, with a short
 * linger) and read streams/states from context. Navigation only adds/removes
 * track subscriptions — the WS + PC stay warm, so re-opening a page is instant.
 *
 * The currently-selected node is kept warm eagerly (ensureConnected) from login,
 * so the very first camera on any page only adds a track over the existing PC.
 *
 * On logout the provider unmounts (or webrtc-teardown fires) → manager.shutdown()
 * closes every ULLFU session immediately (bypassing the resume grace).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNode } from './NodeContext';
import { NodeConnectionManager, type CameraState } from '../services/node-connection-manager';
import { registerWebrtcTeardown } from '../services/webrtc-teardown';

/** Keep a camera subscribed briefly after its last viewer unmounts so a quick
 *  re-open / re-alert reuses the already-flowing track instead of re-running
 *  subscribe signaling. Bridges route changes without holding egress for long. */
const SUBSCRIPTION_LINGER_MS = 15000;

export interface NodeStreamContextValue {
  streams: Map<string, MediaStream | null>;
  states: Map<string, CameraState>;
  /** The keep-warm selected node (connection is held open even with 0 cameras). */
  selectedNodeId: string | null;
  /** Ref-counted subscribe. Safe to call for any node (selected or secondary). */
  subscribe: (nodeId: string, nodeName: string, cameraId: string, label?: string) => void;
  /** Ref-counted unsubscribe (lingers before the track is actually dropped). */
  unsubscribe: (nodeId: string, cameraId: string) => void;
  /** Keep a node's WS+PC warm with zero cameras (multi-site secondary nodes). */
  ensureNode: (nodeId: string, nodeName: string) => void;
  /** Release a keep-warm hold; tears the node down if it has no cameras left. */
  releaseNode: (nodeId: string) => void;
  /** Escape hatch for stall/retry/quality handlers. */
  manager: () => NodeConnectionManager | null;
}

const NodeStreamContext = createContext<NodeStreamContextValue | null>(null);

export function NodeStreamProvider({ children }: { children: ReactNode }) {
  const { selectedNode } = useNode();
  const selectedNodeId = selectedNode?.nodeId ?? null;
  const selectedNodeName = selectedNode?.name ?? '';

  const [streams, setStreams] = useState<Map<string, MediaStream | null>>(new Map());
  const [states, setStates] = useState<Map<string, CameraState>>(new Map());

  const managerRef = useRef<NodeConnectionManager | null>(null);
  // Per-camera ref counts, keyed "nodeId:cameraId".
  const refCounts = useRef<Map<string, number>>(new Map());
  // Cameras currently subscribed on the manager (incl. those in their linger window).
  const addedRef = useRef<Set<string>>(new Set());
  // Pending linger timers, keyed "nodeId:cameraId".
  const lingerTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // ─── Single manager for the whole session ────────────────────────
  useEffect(() => {
    const mgr = new NodeConnectionManager(
      (nId, camId, stream) => setStreams((p) => new Map(p).set(`${nId}:${camId}`, stream)),
      (nId, camId, state) => setStates((p) => new Map(p).set(`${nId}:${camId}`, state)),
      () => { /* node errors surface per-camera via state; no global toast here */ },
    );
    managerRef.current = mgr;

    // Let non-React code (logout / 401) trigger a graceful teardown.
    registerWebrtcTeardown(() => mgr.shutdown());

    return () => {
      registerWebrtcTeardown(null);
      lingerTimers.current.forEach((t) => clearTimeout(t));
      lingerTimers.current.clear();
      refCounts.current.clear();
      addedRef.current.clear();
      // Provider unmounts on logout/redirect — close sessions gracefully.
      mgr.shutdown();
      managerRef.current = null;
    };
  }, []);

  // ─── Keep the SELECTED node warm across the whole session ────────
  // Eagerly open (and hold) its WS+PC so the first camera on any page is instant.
  useEffect(() => {
    const mgr = managerRef.current;
    if (!mgr || !selectedNodeId) return;
    mgr.ensureConnected(selectedNodeId, selectedNodeName);
    const prevId = selectedNodeId;
    return () => {
      // On node switch, drop the keep-warm hold on the previous node (torn down
      // only if it has no active camera subscriptions left).
      managerRef.current?.releaseKeepAlive(prevId);
    };
  }, [selectedNodeId, selectedNodeName]);

  // ─── Ref-counted subscribe / unsubscribe with linger ─────────────
  const subscribe = useCallback((nodeId: string, nodeName: string, cameraId: string, label?: string) => {
    if (!nodeId) return;
    const key = `${nodeId}:${cameraId}`;
    const lt = lingerTimers.current.get(key);
    if (lt) { clearTimeout(lt); lingerTimers.current.delete(key); }
    refCounts.current.set(key, (refCounts.current.get(key) || 0) + 1);
    if (!addedRef.current.has(key)) {
      addedRef.current.add(key);
      managerRef.current?.addCamera(nodeId, nodeName, cameraId, label || cameraId);
    }
  }, []);

  const unsubscribe = useCallback((nodeId: string, cameraId: string) => {
    if (!nodeId) return;
    const key = `${nodeId}:${cameraId}`;
    const n = Math.max(0, (refCounts.current.get(key) || 0) - 1);
    refCounts.current.set(key, n);
    if (n === 0 && !lingerTimers.current.has(key)) {
      const timer = setTimeout(() => {
        lingerTimers.current.delete(key);
        if ((refCounts.current.get(key) || 0) > 0) return; // re-added during linger
        addedRef.current.delete(key);
        refCounts.current.delete(key);
        managerRef.current?.removeCamera(nodeId, cameraId);
      }, SUBSCRIPTION_LINGER_MS);
      lingerTimers.current.set(key, timer);
    }
  }, []);

  const ensureNode = useCallback((nodeId: string, nodeName: string) => {
    if (nodeId) managerRef.current?.ensureConnected(nodeId, nodeName);
  }, []);

  const releaseNode = useCallback((nodeId: string) => {
    if (nodeId) managerRef.current?.releaseKeepAlive(nodeId);
  }, []);

  const value = useMemo<NodeStreamContextValue>(() => ({
    streams,
    states,
    selectedNodeId,
    subscribe,
    unsubscribe,
    ensureNode,
    releaseNode,
    manager: () => managerRef.current,
  }), [streams, states, selectedNodeId, subscribe, unsubscribe, ensureNode, releaseNode]);

  return <NodeStreamContext.Provider value={value}>{children}</NodeStreamContext.Provider>;
}

export function useNodeStream(): NodeStreamContextValue {
  const ctx = useContext(NodeStreamContext);
  if (!ctx) {
    throw new Error('useNodeStream must be used within a NodeStreamProvider');
  }
  return ctx;
}

/** Non-throwing variant for components that may render outside the provider. */
export function useNodeStreamOptional(): NodeStreamContextValue | null {
  return useContext(NodeStreamContext);
}

export interface CameraStreamHandle {
  stream: MediaStream | null;
  /** CameraState from the shared manager ('rtc_established', 'waiting_stream', …). */
  state: CameraState;
  onStalled: () => void;
  onPlaying: () => void;
  onRetry: () => void;
}

/**
 * Subscribe a single camera on the shared connection for the lifetime of a
 * component, and get back its live stream + state + player callbacks. This is
 * the drop-in replacement for pages that used to spin up their own
 * SignalingService + WebRTCService per camera — they now share the session-level
 * warm connection, so opening/closing the page just adds/removes a track.
 */
export function useCameraStream(
  nodeId: string | null | undefined,
  cameraId: string | null | undefined,
  nodeName = '',
  enabled = true,
): CameraStreamHandle {
  const { streams, states, subscribe, unsubscribe, manager } = useNodeStream();

  useEffect(() => {
    if (!enabled || !nodeId || !cameraId) return;
    subscribe(nodeId, nodeName, cameraId);
    return () => unsubscribe(nodeId, cameraId);
  }, [nodeId, cameraId, nodeName, enabled, subscribe, unsubscribe]);

  const key = nodeId && cameraId ? `${nodeId}:${cameraId}` : '';
  return {
    stream: key ? streams.get(key) ?? null : null,
    state: (key ? states.get(key) : undefined) ?? 'pending',
    onStalled: () => { if (nodeId && cameraId) manager()?.handleCameraStall(nodeId, cameraId); },
    onPlaying: () => { if (nodeId && cameraId) manager()?.handleCameraPlaying(nodeId, cameraId); },
    onRetry: () => { if (nodeId && cameraId) manager()?.retryCamera(nodeId, cameraId); },
  };
}
